/**
 * Keep every open screen current, and make a noise when a customer writes.
 *
 * Mounted once at the top of the app rather than inside the Inbox, on purpose:
 * a cashier is usually on the Register, and a message that only announced
 * itself to whoever already had the Inbox open would be a message nobody hears.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { connectLive, playNotification, primeNotificationSound, type LiveEvent } from './live.js';

export function useLiveUpdates(enabled: boolean): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;

    primeNotificationSound();

    const stop = connectLive((event: LiveEvent) => {
      if (event.type !== 'inbox.message') return;

      /*
       * Invalidate rather than patch the cache from the event.
       *
       * The event deliberately carries no message content, so the refetch goes
       * through the same endpoint with the same permission checks as any other
       * read. Writing the event's contents straight into the cache would be
       * faster and would eventually show somebody something their account is
       * not allowed to see.
       */
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      if (typeof event.conversationId === 'string') {
        queryClient.invalidateQueries({ queryKey: ['conversation', event.conversationId] });
      }

      // Only for messages arriving. Our own replies echo back through the same
      // stream so other tills update, and chiming at the person who just typed
      // would train everyone to ignore the sound.
      if (event.direction === 'in') playNotification();
    });

    return stop;
  }, [enabled, queryClient]);
}
